// One of the company's buildings, laid out as the pack (its numbered sections, in order), with its
// progress and history, exclusivity with Renew, and once completed (our team's roof PASS and research
// Approve in Slack), Export report and share links: the exported report is the pack.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Star } from "lucide-react";
import { useApi, type OwnedBuildingDetail, type PackStatus } from "../api";
import { useBreadcrumbLabel } from "../breadcrumbs";
import { PackProgress } from "../components/PackProgress";
import { PreliminaryChecks } from "../components/PreliminaryChecks";
import { RedFlags } from "../components/RedFlags";
import { ReportPanel } from "../components/ReportPanel";
import { PricingModelCard } from "../components/PricingModelCard";
import { LocationMap } from "../components/LocationMap";
import { Opportunity, PropertyFacts, WhoDecides, PropertyResearch, GrantsFunding, solarContracted } from "../components/PackSections";
import { AirportsSection } from "../components/AirportsSection";
import { AiRoofAnalysis } from "../components/AiRoofAnalysis";
import { SiteFacts } from "../components/SiteFacts";
import { EnergyRecords, EnergySpend, EnergyHistory } from "../components/EnergyOpportunity";
import { RoofPlan, SizingSection, RecommendedSystem, RoiDetail, BatteryCarbon, AssumptionsSection, PricingAppendix } from "../components/PackModel";
import { TaxSection, TypicalDaySection, SupplySection, RoofLifeSection, InstallSection, FundingRouteSection, FirstMeetingSection } from "../components/InstallerSections";
import { exclusivityText, longDate } from "./MyBuildingsPage";
import "../dashboard.css";
import "../find.css";
import "../my-buildings.css";
import "../report-sections.css";

const STATUS_LABELS: Record<PackStatus, string> = {
  ordered: "Ordered",
  "in-progress": "In progress",
  completed: "Report ready",
  failed: "Couldn't be prepared · credits refunded",
};

export function MyBuildingPage() {
  const api = useApi();
  const { orderId = "" } = useParams();
  const [b, setB] = useState<OwnedBuildingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getMyBuilding(orderId)
      .then(setB)
      .catch((e: Error) => setError(e.message));
  }, [api, orderId]);

  // While a preliminary check is still running (the roof check just after ordering), look again
  // every 10 seconds, for up to 3 minutes
  const running = Boolean(b?.checks?.some((c) => c.status === "checking"));
  useEffect(() => {
    if (!running) return;
    const started = Date.now();
    const t = window.setInterval(() => {
      if (Date.now() - started > 3 * 60 * 1000) return window.clearInterval(t);
      api
        .getMyBuilding(orderId)
        .then(setB)
        .catch(() => undefined);
    }, 10000);
    return () => window.clearInterval(t);
  }, [api, orderId, running]);

  const p = b?.property;
  // The AI roof check's answer (Section 10, and the roof access in Site and constraints)
  const solarAi = b?.checks?.find((c) => c.key === "solar")?.ai;
  // The breadcrumb names the building, not the order ID in the address
  useBreadcrumbLabel(orderId, p?.name);

  return (
    <div className="page page-wide">
      <Link to="/properties" className="building-back">
        <ArrowLeft size={16} aria-hidden="true" /> Back to My properties
      </Link>

      {error && <div className="flash flash-danger">{error}</div>}

      {b && p && (
        <>
          <div className="page-head">
            <h1>{p.name}</h1>
            <p>
              {b.orderNumber && <span className="mine-order-tag">Order {b.orderNumber}</span>}
              {[...(p.addressLines ?? []).slice(1), p.town, p.postcode].filter(Boolean).join(", ")} · {p.district}
            </p>
          </div>

          <div className="building-grid building-grid-wide">
            <div className="building-main">
              {/* Where the order is, first: the steps our team's checks move it through */}
              <section className="card">
                <h2 className="building-h2">Your order</h2>
                <PackProgress status={b.status} about={b.aboutStatus} />
                {/* The pricing model must be confirmed, even the default (Neil, 11 Oct 2026) */}
                {b.pricingStatus && !b.pricingStatus.confirmed && b.status !== "failed" && (
                  <div className="confirm-model">
                    <span className="pulse-star" aria-hidden="true">
                      <Star size={16} />
                    </span>
                    <div>
                      <b>Confirm the pricing model for this property.</b> Every figure on this page and in the Solar Opportunity Report uses it,
                      so please check it's the right one and confirm it, even if it's your default.{" "}
                      <a href="#pricing-model">Go to the pricing model</a>
                    </div>
                  </div>
                )}
                {/* The history, folded away: two columns, so the dates line up */}
                {b.statusHistory.length > 0 && (
                  <details className="mine-history-wrap">
                    <summary>History</summary>
                    <dl className="mine-history">
                      {[...b.statusHistory].reverse().map((h, i) => (
                        <div key={i}>
                          <dt>{STATUS_LABELS[h.status] ?? h.status}</dt>
                          <dd className="muted">
                            {longDate(h.at)}
                            {h.note ? ` · ${h.note}` : ""}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                )}
              </section>
              {/* Red flags first, whenever there's one: anything that could stop or change the job */}
              <RedFlags flags={b.redFlags} />
              {/* What we've checked so far, and what's still running */}
              <PreliminaryChecks checks={b.checks ?? []} />

              {/* The pack's sections, in the pack's order and numbering (00-initial-commercial-
                  intelligence/pack). What comes from the AI research shows placeholders until our
                  team has approved it in Slack, then fills in. */}
              {/* The pricing model the figures use, just above them: choose, confirm, recalculate, its history */}
              <PricingModelCard b={b} onChange={setB} />
              <hr className="section-sep" />
              {/* Every section of the Solar Opportunity Report, in its order (10-serverless/120-pack/
                  render.py's final assembly: s0 red flags above, then s1 … s13 and the appendix, with
                  the installer pages where the report puts them). Neil, 11 Oct 2026: "the PDF is just
                  an export of the sections in the details page". */}
              <Opportunity b={b} />
              <PropertyFacts b={b} />
              {p.location && (
                <LocationMap
                  lat={p.location.lat}
                  lng={p.location.lng}
                  district={p.district}
                  address={[...(p.addressLines ?? []).slice(1), p.town, p.postcode].filter(Boolean).join(", ")}
                  nation={b.installer?.tax?.nation}
                  gridOperator={b.site?.grid?.covered ? b.site.grid.operator : null}
                />
              )}
              {b.site && <SiteFacts s={b.site} kicker="Section 2 continued" roofAi={solarAi?.roof} />}
              <AirportsSection b={b} />
              <EnergyRecords b={b} />
              <EnergySpend b={b} />
              <EnergyHistory b={b} />
              <WhoDecides b={b} />
              <RoofPlan b={b} />
              <SizingSection b={b} />
              <RecommendedSystem b={b} />
              <RoiDetail b={b} />
              <TaxSection b={b} />
              <BatteryCarbon b={b} />
              <TypicalDaySection b={b} />
              <SupplySection b={b} />
              {/* Section 10: the AI roof analysis, from the solar check */}
              {solarAi && (
                <AiRoofAnalysis
                  ai={solarAi}
                  imageryDate={b.checks?.find((c) => c.key === "imagery")?.detail.match(/from (\w+ \d{4})/)?.[1]}
                  kicker="Section 10"
                  contracted={solarContracted(b.about)}
                  teamNote={b.checks?.find((c) => c.key === "solar")?.detail}
                />
              )}
              <RoofLifeSection b={b} />
              <InstallSection b={b} />
              <PropertyResearch b={b} />
              <GrantsFunding b={b} />
              <FundingRouteSection b={b} />
              <FirstMeetingSection b={b} />
              <AssumptionsSection b={b} />
              <PricingAppendix b={b} />

              {/* What kinds of data this page uses, and the licence credits that must be shown */}
              <p className="pk-data muted">
                This page and its report combine open data (including government open data and openly licensed community data), data from third parties, data licensed to us, and our own curated and derived data, with our analysis and AI checks. Data sources and attributions:{" "}
                <a href="/data-attributions.txt" target="_blank" rel="noreferrer">
                  data-attributions
                </a>
                .
              </p>
            </div>

            {/* Export report, share links and exclusivity */}
            <aside className="card building-order">
              <h2 className="building-h2">Solar Opportunity Report</h2>
              {b.status === "completed" ? (
                <>
                  {/* Export the page as the pack PDF, and share it (completed orders only) */}
                  {/* Keyed on the report's state, so a recalculation (which outdates it) shows at once */}
                  <ReportPanel key={`${b.report?.generatedAt}-${b.report?.outdated}-${Boolean(b.pricingStatus?.confirmed)}`} orderId={b.orderId} initial={b.report}
                    pricingConfirmed={!b.pricingStatus || Boolean(b.pricingStatus.confirmed)} />
                </>
              ) : (
                <p className="card-sub">
                  {b.status === "failed"
                    ? "This order couldn't be completed, so your credits were refunded."
                    : "You can export and share the Solar Opportunity Report once our team has checked the roof and the research."}
                </p>
              )}
              {/* Label above, status below: the side panel is narrow (Neil, 14 Oct 2026) */}
              <div className="building-cost is-stacked">
                <span>Exclusivity</span>
                <b className={`mine-excl is-${b.status === "failed" ? "released" : b.exclusivity.state}`}>{exclusivityText(b)}</b>
              </div>
              {b.exclusivity.canRenew && (
                <Link to={`/find/${encodeURIComponent(b.ref)}`} state={{ from: "" }} className="btn btn-secondary building-cta">
                  <RefreshCw size={16} aria-hidden="true" /> Renew for 6 months
                </Link>
              )}
              {!b.exclusivity.canRenew && b.exclusivity.state === "running" && (
                <p className="building-small muted">You can renew it from 30 days before it ends.</p>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
